import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { colors } from '@tempmee/tailwind';

import { remoteConfigReady } from '~/api/firebaseConfig';
import { SaltclickChatbot } from '~/constants/ff.posthog';
import {
  buildSaltclickChatIdentity,
  endSaltclickChatSession,
  fetchSaltclickIdentityToken,
  getSaltclickVerifiedForUserId,
  ISaltclickChatIdentity,
  setSaltclickVerifiedForUserId,
  toSaltclickPrechatFields,
} from '~/domain/SaltclickChat';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useUserSelector } from '~/hooks/useUserSelector';
import { fireHelpChatbotButtonTapped } from '~/posthogs/events';
import { routes } from '~/routes';
import { TRootState } from '~/store/types';
import { RemoteConfigUtils } from '~/utils/RemoteConfigUtils';

const SCRIPT_ID = 'saltclick-bootstrap';
// Salesforce can fail silently (blocked script, CORS on its own subrequests),
// so the skeleton gets a deadline instead of pulsing forever.
const SKELETON_TIMEOUT_MS = 20000;
/**
 * How long after the ready event to check that a launcher actually painted. Ready
 * fires before the widget renders, so an immediate check would always fail.
 */
const PAINT_CHECK_DELAY_MS = 5000;
const STYLE_ID = 'saltclick-visibility';
const VISIBLE_CLASS = 'saltclick-visible';
const READY_CLASS = 'saltclick-ready';

/**
 * Salesforce Embedded Messaging renders everything (button + chat) into a
 * `#embedded-messaging` container and can only be init'd once per page load,
 * so visibility is driven by a body class instead of unloading the widget.
 * The widget renders async after init; the CSS rule keeps it hidden on any
 * route where the Help page isn't mounted, even if init finishes after the
 * user has navigated away.
 *
 * The `::after` skeleton stands in for the launcher until the widget paints —
 * only on Help (`VISIBLE_CLASS`), and only before ready. A landing straight on
 * /help still waits on Salesforce, so the corner shows a placeholder rather
 * than nothing. ponytail: a pseudo-element keeps this out of the React tree;
 * promote it to a real component if it ever needs interaction.
 */
const ensureVisibilityStyle = (): void => {
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = `
    body:not(.${VISIBLE_CLASS}) #embedded-messaging { display: none !important; }
    body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after {
      content: '';
      position: fixed;
      bottom: 1.25rem;
      right: 1.25rem;
      width: 3.75rem;
      height: 3.75rem;
      border-radius: 9999px;
      background: ${colors.neutral[200]};
      animation: saltclick-skeleton-pulse 1.5s ease-in-out infinite;
    }
    @keyframes saltclick-skeleton-pulse { 50% { opacity: 0.4; } }
    @media (prefers-reduced-motion: reduce) {
      body.${VISIBLE_CLASS}:not(.${READY_CLASS})::after { animation: none; }
    }
  `;
  document.head.appendChild(style);
};

/**
 * Embeds the Saltclick (Salesforce Embedded Messaging) chatbot. Called once
 * from the authenticated layout so the widget boots in the background right
 * after login and the launcher is already painted (just hidden) by the time
 * the user opens Help — no load-then-pop. Gated by the
 * `GOTU-4448-saltclick-chatbot` PostHog flag. GOTU-5243.
 *
 * The session is verified rather than anonymous: auth-service mints a short-lived
 * JWT for the logged-in account and the widget is told to trust it, which is what
 * separates one user's conversation from the next in a shared tab. GOTU-5918.
 */
export const useSaltclickChat = (): void => {
  const flagEnabled = useFeatureFlag(SaltclickChatbot);
  // The office being acted as — under an office switch, the selected child.
  // This is what pre-chat reports, since it is who the chat is about.
  const user = useUserSelector();
  const officeId: string | undefined = user?.id;
  // The account the access token belongs to, which is who auth-service mints for
  // and therefore what a verified session is keyed on. Same as `officeId` unless
  // a parent is acting as one of its child offices.
  const accountId = useSelector((state: TRootState) => state.user.user?.id);
  // Logged-in users only — never show the widget when Help is reached from
  // login/sign-up (GOTU-5243).
  const enabled = flagEnabled && !!user;
  const isHelpRoute = useLocation().pathname === routes.help;

  const [isMessagingReady, setIsMessagingReady] = useState(false);

  // Read from event handlers (ready, token expiry) that outlive the render they
  // were registered in, so they need the current user, not a captured one.
  const identityRef = useRef<ISaltclickChatIdentity | null>(null);
  const accountIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    identityRef.current = buildSaltclickChatIdentity(user);
    accountIdRef.current = accountId;
  }, [accountId, user]);

  /** The account a token request is currently out for, if any. */
  const requestedForRef = useRef<string | null>(null);
  /**
   * Fetches a token for whoever is logged in now and hands it to the widget.
   *
   * Every trigger — first ready, a fresh login, token expiry, a closed
   * conversation — re-calls the endpoint rather than reusing a held token. A token
   * is short-lived (900s) and bound to the conversation it opened, so a cached one
   * is the wrong thing to re-hand; and keeping none means each trigger is
   * self-contained, instead of the outcome depending on which of two paths reached
   * the shared token first.
   *
   * The marker is the only thing that makes this cheap to call: already verified as
   * this account means there is nothing to do, so the repeat triggers cost nothing.
   */
  const verifySession = useCallback(async (): Promise<void> => {
    const account = accountIdRef.current;
    if (!account) return;

    // Nothing to hand a token to yet. `userVerificationAPI` appears with the
    // widget, and the ready event re-runs this — so this is the normal early path,
    // not a failure (`onReady` reports a ready widget that still lacks it).
    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (!verificationApi) return;

    if (getSaltclickVerifiedForUserId() === account) return;

    // One request in flight per account: this runs from the mount, from the ready
    // event, from token expiry and from a closed conversation, which overlap.
    if (requestedForRef.current === account) return;
    requestedForRef.current = account;

    try {
      // No argument: auth-service mints for whoever the access token belongs to,
      // so the token's subject is the answer, not something we ask for.
      const identityToken = await fetchSaltclickIdentityToken();

      // A token that arrives after the account changed belongs to nobody we can
      // use it for, so it is dropped rather than handed over.
      if (accountIdRef.current !== account) return;

      // Belt and braces on the one thing that must not go wrong: the token has
      // to be for the account we think is logged in. Anything else would file
      // the conversation under someone else.
      if (identityToken.sub && identityToken.sub !== account) {
        console.error(
          `[Saltclick] identity token was minted for ${identityToken.sub}, ` +
            `not ${account} — leaving the chat session anonymous`,
        );

        return;
      }

      // Another trigger verified this account while the request was out.
      if (getSaltclickVerifiedForUserId() === account) return;

      verificationApi.setIdentityToken({
        identityTokenType: identityToken.tokenType ?? 'JWT',
        identityToken: identityToken.token,
      });
      setSaltclickVerifiedForUserId(account);
      // Logged because the failure this replaced was silent: an anonymous session
      // looks identical to a verified one from the outside, so "did the token
      // land?" was otherwise unanswerable from a support session. The token itself
      // is never logged — it is a bearer credential; `kid` and `expiresIn` identify
      // it without carrying anything sensitive.
      console.log(
        `[Saltclick] Verified chat session for ${account} (kid ${identityToken.kid ?? 'unknown'}` +
          `, expires in ${identityToken.expiresIn ?? '?'}s)`,
      );
    } catch (err) {
      // Leaves the session anonymous rather than taking the whole widget down.
      // Includes the 503 an environment returns until its signing key is
      // provisioned, and the 400 for an account whose user type Salesforce has no
      // mapping for. Note this deployment runs `authMode: "Auth"`, where an
      // anonymous session cannot start a conversation at all — the launcher is
      // there but inert, so this line is the only explanation of why.
      console.error('[Saltclick] Could not mint a chat identity token: ', err);
    } finally {
      // Only clear the slot this call owns; a newer request for a different
      // account has already claimed it.
      if (requestedForRef.current === account) requestedForRef.current = null;
    }
  }, []);

  /**
   * Stages the hidden pre-chat fields for the NEXT conversation.
   *
   * Salesforce takes these only after the ready event and before a conversation
   * begins, and a late write is dropped in silence — no error, no clue. So every
   * conversation needs its own, which is why this is callable rather than only an
   * effect: after a chat ends, the next conversation has not begun, and that is the
   * one window left to stage it in. Without that write the next conversation starts
   * with nothing staged and stops on the pre-chat step.
   *
   * `gtAdminPanelId` is the field Salesforce keys session control off; `userType`
   * tells an agent which kind of account they are talking to.
   */
  const sendPrechatFields = useCallback((): void => {
    const identity = identityRef.current;
    if (!identity) return;

    const prechatApi = window.embeddedservice_bootstrap?.prechatAPI;
    if (!prechatApi) {
      // Before ready the widget simply does not exist yet, and `isMessagingReady`
      // brings the effect below back — so only a *ready* widget without `prechatAPI`
      // is an anomaly. Reported for the same reason as the missing
      // `userVerificationAPI` above: the conversation would otherwise reach an agent
      // with no office on it and nothing would say why.
      if (window.embeddedservice_bootstrap) {
        console.error(
          '[Saltclick] Widget is ready but exposes no prechatAPI — no pre-chat ' +
            'form is configured on this Salesforce deployment, so the conversation ' +
            'carries no gtAdminPanelId',
        );
      }

      return;
    }

    const fields = toSaltclickPrechatFields(identity);
    prechatApi.setHiddenPrechatFields(fields);
    // Field *names* only: the identity holds PII (`email`) that this must never
    // put in a console. Which keys went is the useful part anyway — Salesforce
    // discards undeclared fields silently, so `DECLARED_PRECHAT_FIELDS` is what
    // this line lets you confirm against their config.
    console.log(
      `[Saltclick] Sent hidden pre-chat fields for ${identity.gtAdminPanelId}: ` +
        `${Object.keys(fields).join(', ')}`,
    );
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;

    ensureVisibilityStyle();
    window.addEventListener('onEmbeddedMessagingConversationOpened', fireHelpChatbotButtonTapped);

    // The class swaps the skeleton for the real launcher (or drops it when the
    // widget never arrives). It lives on the body, not in state, so it survives
    // the hook remounting on a fresh login.
    const hideSkeleton = () => document.body.classList.add(READY_CLASS);

    /**
     * What is known about the widget right now, for the lines below.
     *
     * Each of these is a different reason for an empty corner, and they are
     * indistinguishable from the outside: the script never loaded, it loaded but
     * `init` was never reached, `init` ran but the widget never said it was ready, or
     * everything worked and our own CSS is hiding it.
     */
    const widgetState = () =>
      [
        `script ${document.getElementById(SCRIPT_ID) ? 'present' : 'missing'}`,
        `bootstrap ${window.embeddedservice_bootstrap ? 'present' : 'missing'}`,
        `container ${document.getElementById('embedded-messaging') ? 'present' : 'missing'}`,
        `body classes ${document.body.className || '(none)'}`,
      ].join(', ');

    /**
     * Reports a widget that never rendered anything.
     *
     * Ready is not the same as visible: the event says the widget booted, not that it
     * painted a launcher. A ready widget with an empty container is the "chat is just
     * not there" report, and until now it produced no log of any kind — the skeleton
     * timed out, the corner went empty, and nothing said why.
     */
    let paintCheckTimer: number | undefined;
    const checkWidgetPainted = () => {
      const container = document.getElementById('embedded-messaging');
      if (container?.childElementCount) return;

      console.error(
        `[Saltclick] Widget reported ready but rendered nothing — ${widgetState()}`,
      );
    };

    const onReady = () => {
      hideSkeleton();
      setIsMessagingReady(true);
      console.log('[Saltclick] Widget ready');
      // Late enough for the launcher to have painted, since ready fires before it.
      paintCheckTimer = window.setTimeout(checkWidgetPainted, PAINT_CHECK_DELAY_MS);
      // `userVerificationAPI` only exists when User Verification is enabled on the
      // Salesforce deployment. Without it there is nothing to hand a token to, so
      // say so once — otherwise the session is quietly anonymous and the missing
      // token request looks like a bug on this side.
      if (!window.embeddedservice_bootstrap?.userVerificationAPI) {
        console.error(
          '[Saltclick] Widget is ready but exposes no userVerificationAPI — User ' +
            'Verification is not enabled on this Salesforce deployment, so the chat ' +
            'session stays anonymous',
        );
      }
    };
    window.addEventListener('onEmbeddedMessagingReady', onReady);
    // The deadline used to drop the skeleton in silence, which is the worst version of
    // this failure: the corner simply goes empty, with nothing in the console to say
    // the widget never arrived or how far it got.
    const skeletonTimer = window.setTimeout(() => {
      hideSkeleton();
      console.error(
        `[Saltclick] Widget never became ready in ${SKELETON_TIMEOUT_MS}ms — ${widgetState()}`,
      );
    }, SKELETON_TIMEOUT_MS);

    // The widget asks for a token whenever the one it holds stops being usable —
    // expiry, or the reset that follows clearSession.
    const onIdentityTokenExpired = () => {
      // Dropping the marker is what lets `verifySession` past its already-verified
      // guard, and is true in its own right: the session stops counting as verified
      // until a fresh token is handed over.
      setSaltclickVerifiedForUserId(undefined);
      void verifySession();
    };
    window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', onIdentityTokenExpired);

    let cancelled = false;
    // Remote config is fetched fire-and-forget at startup, so on a first login
    // the values aren't activated yet — wait for them instead of bailing out.
    void remoteConfigReady.then(() => {
      if (cancelled || document.getElementById(SCRIPT_ID)) return;

      // Same SALTCLICK_CHAT_CONFIG remote-config key as mobile; fails closed
      // (no bundled default) when unset or incomplete.
      const { orgId, deploymentName, siteUrl, scrt2Url } =
        RemoteConfigUtils.getSaltclickChatConfig() ?? {};
      if (!orgId || !deploymentName || !siteUrl || !scrt2Url) {
        console.error('[Saltclick] Missing or incomplete SALTCLICK_CHAT_CONFIG remote config');
        hideSkeleton();
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${siteUrl}/assets/js/bootstrap.min.js`;
      script.onerror = () => {
        console.error('[Saltclick] Failed to load bootstrap script');
        hideSkeleton();
      };
      script.onload = () => {
        const bootstrap = window.embeddedservice_bootstrap;
        if (!bootstrap) {
          console.error('[Saltclick] embeddedservice_bootstrap not available after script load');
          hideSkeleton();
          return;
        }
        try {
          bootstrap.settings.language = 'en_US';
          // Otherwise "the script loaded but init was never reached" and "init ran and
          // the widget never appeared" look identical from a console.
          console.log(`[Saltclick] Calling init for ${deploymentName} @ ${orgId}`);
          bootstrap.init(orgId, deploymentName, siteUrl, { scrt2URL: scrt2Url });
        } catch (err) {
          console.error('Error loading Embedded Messaging: ', err);
          hideSkeleton();
        }
      };
      document.body.appendChild(script);
    });

    return () => {
      cancelled = true;
      window.clearTimeout(skeletonTimer);
      window.clearTimeout(paintCheckTimer);
      window.removeEventListener(
        'onEmbeddedMessagingConversationOpened',
        fireHelpChatbotButtonTapped,
      );
      window.removeEventListener('onEmbeddedMessagingReady', onReady);
      window.removeEventListener(
        'onEmbeddedMessagingIdentityTokenExpired',
        onIdentityTokenExpired,
      );
    };
    // `sendPrechatFields` is stable, but it is a dependency of the end handler this
    // effect registers, so it belongs here rather than being silenced.
  }, [enabled, sendPrechatFields, verifySession]);

  // Verified identity. `userVerificationAPI` only exists once messaging is ready,
  // so it is the real gate — `isMessagingReady` is just the signal to re-run.
  // Keyed on the account rather than the acting office: switching between one's
  // own offices is the same person and must not restart the conversation, while a
  // different login in the same tab must. Also covers a fresh login where the
  // already-init'd widget never fires the ready event again.
  useEffect(() => {
    if (!enabled || !accountId) return;

    if (getSaltclickVerifiedForUserId() === accountId) return;

    const verificationApi = window.embeddedservice_bootstrap?.userVerificationAPI;
    if (verificationApi && getSaltclickVerifiedForUserId()) {
      // A session verified as someone else has to be ended before this account
      // can take over — the fallback for a handover logout did not get to clean
      // up after (a crashed tab, or a session dropped without LOGOUT_ACTION).
      void endSaltclickChatSession().then(verifySession);

      return;
    }

    void verifySession();
  }, [accountId, enabled, isMessagingReady, verifySession]);

  // Re-stages when the user changes (office switch, or a fresh login in the same tab)
  // and once the widget is ready to accept them.
  useEffect(() => {
    if (!officeId) return;

    sendPrechatFields();
  }, [isMessagingReady, officeId, sendPrechatFields]);

  useEffect(() => {
    if (!isHelpRoute) return undefined;

    if (!enabled) {
      // Standing on Help with the widget hidden, which is correct on the way out of a
      // session and wrong at any other time. Worth a line because it looks exactly like
      // a broken widget: the launcher is not there and nothing else explains it. The
      // widget is init'd once per document and cannot be re-created, so if `enabled`
      // went false transiently — a user record briefly absent from redux, say — the
      // corner stays empty even though everything loaded.
      console.log(
        '[Saltclick] On Help but the widget is disabled ' +
          `(flag ${flagEnabled ? 'on' : 'off'}, user ${user ? 'present' : 'missing'}) — hiding it`,
      );

      return undefined;
    }

    document.body.classList.add(VISIBLE_CLASS);

    return () => document.body.classList.remove(VISIBLE_CLASS);
  }, [enabled, flagEnabled, isHelpRoute, user]);
};
